import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartColumn } from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import dataService from '../../services/DataService';

// Enregistrement des composants Chart.js nécessaires
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const BillSynthesis = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [chartData, setChartData] = useState({
    labels: [''],  // Remplacer 'Montants' par une chaîne vide
    datasets: [
      {
        label: 'Devis Signés',
        data: [0],
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      },
      {
        label: 'Montant Facturé',
        data: [0],
        backgroundColor: 'rgba(255, 206, 86, 0.5)',
      },
      {
        label: 'Montant Payé',
        data: [0],
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      },
      {
        label: 'TVA à Déclarer',
        data: [0],
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      }
    ]
  });

  const calculateSynthesis = useCallback(async () => {
    try {
      let totalSignedQuotes = 0;
      let totalBilled = 0;
      let totalPaid = 0;
      let totalVAT = 0;

      const quotes = await dataService.db.quotes.toArray();

      const vat20Setting = await dataService.getByField('settings', 'name', 'TVA20');
      if(!vat20Setting) return null;
      
      for (const quote of quotes) {
        if (quote.status === 'Signed') {
          // Obtenir la date du devis
          const quoteDate = new Date(quote.timeStamp.year, 
                                   parseInt(quote.timeStamp.month) - 1, 
                                   quote.timeStamp.day);

          // Vérifier si le devis est dans la plage de dates
          let includeQuote = true;
          if (startDate || endDate) {
            const startDateTime = startDate ? new Date(startDate) : null;
            const endDateTime = endDate ? new Date(endDate) : null;

            if (startDateTime && quoteDate < startDateTime) includeQuote = false;
            if (endDateTime && quoteDate > endDateTime) includeQuote = false;
          }

          if (includeQuote) {
            const quoteTotal = await quote.getTotal();
            totalSignedQuotes += quoteTotal;

            // Récupérer toutes les factures pour ce devis
            const allBills = await dataService.db.bills
              .where('quoteId')
              .equals(quote.id)
              .toArray();

            // Calculer le montant facturé (toutes les factures envoyées et payées)
            const billedBills = allBills.filter(bill => bill.status === 'Sent' || bill.status === 'Paid');
            const quoteBilledAmount = billedBills.reduce((total, bill) => {
              return total + (typeof bill.currentBillAmount === 'number' ? bill.currentBillAmount : 0);
            }, 0);
            totalBilled += quoteBilledAmount;

            // Calculer le montant payé (toutes les factures avec statut "Paid")
            const paidBills = allBills.filter(bill => bill.status === 'Paid');
            const quotePaidAmount = paidBills.reduce((total, bill) => {
              return total + (typeof bill.currentBillAmount === 'number' ? bill.currentBillAmount : 0);
            }, 0);

            totalPaid += quotePaidAmount;

            const vat20 = vat20Setting ? parseFloat(vat20Setting.data) : 0;
            totalVAT += quotePaidAmount * vat20 / 100;
          }
        }
      }

      // Mise à jour complète du state avec les nouvelles données
      setChartData({
        labels: [''],  // Remplacer 'Montants' par une chaîne vide
        datasets: [
          {
            label: 'Devis Signés',
            data: [totalSignedQuotes],
            backgroundColor: 'rgba(53, 162, 235, 0.5)',
          },
          {
            label: 'Montant Facturé',
            data: [totalBilled],
            backgroundColor: 'rgba(255, 206, 86, 0.5)',
          },
          {
            label: 'Montant Payé',
            data: [totalPaid],
            backgroundColor: 'rgba(75, 192, 192, 0.5)',
          },
          {
            label: 'TVA à Déclarer',
            data: [totalVAT],
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
          }
        ]
      });
    } catch (error) {
      console.error('Erreur dans calculateSynthesis:', error);
    }
  }, [startDate, endDate]);

  // Ajout d'un useEffect pour les dates
  useEffect(() => {
    calculateSynthesis();
  }, [startDate, endDate, calculateSynthesis]);

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
        text: 'Synthèse Financière',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}`;
          }
        }
      },
      datalabels: {
        anchor: 'end',
        align: 'bottom',
        formatter: (value) => value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }),
        color: '#555',
        font: {
          weight: 'bold'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
          }
        }
      }
    }
  };

  return (
    <div className="panel" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="panel-heading">
        <div className="panel-header-content">
          <span className="icon-text">
            <span className="icon">
              <FontAwesomeIcon icon={faChartColumn} />
            </span>
            <span> Synthèse Financière</span>
          </span>
          <div className="panel-header-actions">
            <input
              type="date"
              className="input is-small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <input
              type="date"
              className="input is-small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="panel-block" style={{ flex: 1, overflow: 'hidden' }}>
        <div className="panel-content" style={{ 
          width: '98%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '0.75rem 0.75rem 0.75rem 0.75rem'  // padding: haut droite bas gauche
        }}>
          <Bar options={options} data={chartData} style={{ maxHeight: '100%' }} />
        </div>
      </div>
      <div className="panel-footer"></div>
    </div>
  );
};

export default BillSynthesis;
